import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { RsvpResponseValue } from '@peles/shared';
import type { GuestListQuery } from '../../../api/eventGuests';

/** How the list is grouped: headings over the rows, the server sorting to match. */
export type GuestGroupBy = 'none' | 'side' | 'familyGroup' | 'table' | 'response';

export const GUEST_PAGE_SIZE = 50;

const RESPONSES: readonly RsvpResponseValue[] = ['YES', 'NO', 'MAYBE', 'PENDING'];
const SOURCES = ['PRODUCER', 'CLIENT', 'IMPORT', 'PUBLIC_LINK'] as const;
const GROUPS: readonly GuestGroupBy[] = ['none', 'side', 'familyGroup', 'table', 'response'];
const SORTS = ['name', 'createdAt', 'table', 'side', 'familyGroup'] as const;

/** The server's sort for a grouping, so each group's rows arrive together. */
const SORT_FOR_GROUP: Record<Exclude<GuestGroupBy, 'none'>, GuestListQuery['sort']> = {
  side: 'side',
  familyGroup: 'familyGroup',
  table: 'table',
  response: 'response',
};

export interface GuestListState {
  q: string;
  response: RsvpResponseValue[];
  side: string[];
  tags: string[];
  familyGroup: string[];
  source: Array<(typeof SOURCES)[number]>;
  seated: '' | 'yes' | 'no';
  group: GuestGroupBy;
  sort: NonNullable<GuestListQuery['sort']>;
  dir: 'asc' | 'desc';
  page: number;
}

const list = (params: URLSearchParams, key: string): string[] =>
  (params.get(key) ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

function oneOf<T extends string>(value: string | null, allowed: readonly T[], fallback: T): T {
  return value && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

/** The list's state read from the address — anything unknown is dropped. */
export function readGuestListState(params: URLSearchParams): GuestListState {
  const page = Number(params.get('page'));
  return {
    q: params.get('q') ?? '',
    response: list(params, 'rsvp').filter((v): v is RsvpResponseValue =>
      (RESPONSES as readonly string[]).includes(v),
    ),
    side: list(params, 'side'),
    tags: list(params, 'tags'),
    familyGroup: list(params, 'group'),
    source: list(params, 'source').filter((v): v is (typeof SOURCES)[number] =>
      (SOURCES as readonly string[]).includes(v),
    ),
    seated: oneOf(params.get('seated'), ['', 'yes', 'no'] as const, ''),
    group: oneOf(params.get('groupBy'), GROUPS, 'none'),
    sort: oneOf(params.get('sort'), SORTS, 'name'),
    dir: oneOf(params.get('dir'), ['asc', 'desc'] as const, 'asc'),
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
}

/** What the server is asked for, for a state. */
export function guestListQuery(state: GuestListState): GuestListQuery {
  return {
    page: state.page,
    pageSize: GUEST_PAGE_SIZE,
    sort: state.group === 'none' ? state.sort : SORT_FOR_GROUP[state.group],
    dir: state.dir,
    q: state.q.trim() || undefined,
    response: state.response,
    side: state.side,
    tags: state.tags,
    familyGroup: state.familyGroup,
    source: state.source,
    seated: state.seated || undefined,
  };
}

const KEY_FOR: Record<keyof Omit<GuestListState, 'page'>, string> = {
  q: 'q',
  response: 'rsvp',
  side: 'side',
  tags: 'tags',
  familyGroup: 'group',
  source: 'source',
  seated: 'seated',
  group: 'groupBy',
  sort: 'sort',
  dir: 'dir',
};

const DEFAULTS: Partial<Record<string, string>> = { groupBy: 'none', sort: 'name', dir: 'asc' };

/**
 * The guest list's filters, grouping, sort and page, kept in the address
 * (`?rsvp=&side=&tags=&group=&seated=&q=&groupBy=&page=`) so a filtered list
 * can be bookmarked, shared and survives a reload. Changing anything but the
 * page goes back to the first page. Other params on the address (a workspace's
 * own) are left alone.
 */
export function useGuestListParams() {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => readGuestListState(params), [params]);

  const update = useCallback(
    (patch: Partial<GuestListState>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [field, value] of Object.entries(patch)) {
            if (field === 'page') continue;
            const key = KEY_FOR[field as keyof typeof KEY_FOR];
            const text = Array.isArray(value) ? value.join(',') : String(value ?? '');
            if (!text || DEFAULTS[key] === text) next.delete(key);
            else next.set(key, text);
          }
          const page = 'page' in patch ? (patch.page ?? 1) : 1;
          if (page > 1) next.set('page', String(page));
          else next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const clearFilters = useCallback(
    () =>
      update({ q: '', response: [], side: [], tags: [], familyGroup: [], source: [], seated: '' }),
    [update],
  );

  const hasFilters =
    !!state.q.trim() ||
    state.response.length > 0 ||
    state.side.length > 0 ||
    state.tags.length > 0 ||
    state.familyGroup.length > 0 ||
    state.source.length > 0 ||
    !!state.seated;

  return { state, update, clearFilters, hasFilters };
}
